import { useState, type FormEvent, type JSX } from "react";
import { AlertCircle, ArrowRight, LoaderCircle, LockKeyhole } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";

import framnaPagePatternUrl from "../../../framna-agent-brand-kit/assets/patterns/Singles/Black/SVG/Framna_Frampersand_RGB_Single_Frampersands_Black_03.svg";
import framnaHeroPatternUrl from "../../../framna-agent-brand-kit/assets/patterns/Mural/White/SVG/Framna_Frampersand_RGB_Pattern_White_02.svg";
import framnaWordmarkBlackUrl from "../../../framna-agent-brand-kit/assets/logo/RGB/SVG/Framna_Logo_RGB_Black.svg";
import framnaSymbolWhiteUrl from "../../../framna-agent-brand-kit/assets/symbol/RGB/SVG/Framna_Symbol_RGB_White.svg";

const defaultEmail = "ole@framna.com";

type LoginScreenProps = {
  onAuthenticated?: () => void;
};

export const LoginScreen = ({ onAuthenticated }: LoginScreenProps): JSX.Element => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    const { error } = await authClient.signIn.email({
      email,
      password
    });

    setIsSubmitting(false);

    if (error) {
      setErrorMessage(error.message || "Unable to sign in.");
      return;
    }

    setPassword("");
    onAuthenticated?.();
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-background text-black">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url(${framnaPagePatternUrl})`,
          backgroundPosition: "top 2rem right 2rem",
          backgroundRepeat: "no-repeat",
          backgroundSize: "min(11rem, 18vw)"
        }}
      />
      <div className="relative mx-auto grid min-h-screen max-w-7xl gap-6 px-5 py-5 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-8">
        <section className="relative flex min-h-[18rem] flex-col justify-between overflow-hidden rounded-[2rem] bg-primary p-6 text-white shadow-[0_36px_90px_-42px_rgba(0,0,0,0.45)] sm:p-8 lg:min-h-0 lg:p-10">
          <div
            className="pointer-events-none absolute inset-0 opacity-100"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.04)), url(${framnaHeroPatternUrl})`,
              backgroundPosition: "center, right -6rem bottom -5rem",
              backgroundRepeat: "no-repeat",
              backgroundSize: "auto, min(28rem, 58%)"
            }}
          />
          <div className="relative flex items-center justify-between gap-4">
            <img src={framnaSymbolWhiteUrl} alt="" aria-hidden="true" className="h-11 w-11" />
            <span className="rounded-full border border-white/20 bg-black/10 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-white/82">
              Internal Access
            </span>
          </div>
          <div className="relative max-w-xl space-y-5">
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-white/72">FramnaBid Workspace</p>
            <h1 className="font-editorial text-5xl leading-[0.88] tracking-[-0.05em] text-balance sm:text-6xl lg:text-7xl">
              Win by product.
              <br />
              Shape the winning proposal.
            </h1>
          </div>
          <div className="relative max-w-[16rem] text-sm text-white/88">
            <div className="rounded-[1.5rem] border border-white/16 bg-white/10 p-4 backdrop-blur-sm">
              <p className="text-[0.68rem] uppercase tracking-[0.28em] text-white/65">Need support? Contact</p>
              <p className="mt-2 text-lg leading-snug">{defaultEmail}</p>
            </div>
          </div>
        </section>

        <section className="flex items-center justify-center">
          <div className="w-full max-w-xl rounded-[2rem] border border-black/10 bg-white/92 p-6 shadow-[0_26px_70px_-40px_rgba(0,0,0,0.34)] backdrop-blur-sm sm:p-8 lg:p-10">
            <div className="space-y-8">
              <div className="space-y-5">
                <img src={framnaWordmarkBlackUrl} alt="Framna" className="h-10 w-auto sm:h-12" />
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-black/50">Secure Login</p>
                  <h2 className="font-editorial text-4xl leading-[0.94] tracking-[-0.045em] sm:text-5xl">
                    Enter FramnaBid.
                  </h2>
                </div>
              </div>

              <form className="space-y-5" onSubmit={(event) => void onSubmit(event)}>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.22em] text-black/58" htmlFor="login-email">
                    Email
                  </label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="h-12 rounded-2xl border-black/12 bg-background px-4 text-base shadow-none"
                    placeholder="name@framna.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.22em] text-black/58" htmlFor="login-password">
                    Password
                  </label>
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="h-12 rounded-2xl border-black/12 bg-background px-4 text-base shadow-none"
                    placeholder="Enter password"
                    required
                  />
                </div>

                {errorMessage ? (
                  <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" />
                    <p>{errorMessage}</p>
                  </div>
                ) : null}

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="h-12 w-full rounded-2xl bg-black text-base text-white hover:bg-black/88"
                >
                  {isSubmitting ? <LoaderCircle className="size-4 animate-spin" /> : <LockKeyhole className="size-4" />}
                  <span>Sign In</span>
                  <ArrowRight className="size-4" />
                </Button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};
